import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import useNotifier from '@softwaredev_cegeka/stix-notifier/dist/hooks/useNotifier'
import ThemeContext from '@softwaredev_cegeka/stix-core/dist/components/ThemeProvider/ThemeContext'
import { useMfe } from '@softwaredev_cegeka/microfrontend-utils/dist/react'
import mapTranslations from 'src/utils/mfe/mapTranslations'
import {
    isAllAllowed,
    isAllowed,
    isAnyAllowed,
    isGlobalAdmin,
} from 'src/utils/permissions'
import { reverse } from 'src/routes/config'
import { createMfeLink, CROSS_MFE_LINKS } from 'src/routes/crossMfeLink'
import useUserData from 'src/hooks/useUserData'
import { PageHeaderContext } from 'src/components/PageHeader/pageHeaderContext'
import useMfeTranslations from 'src/hooks/useMfeTranslations'
import useTranslations from 'src/hooks/useTranslations'

/**
 * @param {"mfe-example"|"mfe-auth"} mfeKey
 * @param {string} mfeBasePath path to host the MFE on.
 * @param {Node} rootNode DOM node to mount the app into.
 * @param {{[mfeConfig]: Object, [mfeUtilities]: Object}} [extraMountParams] extra properties passed to the `mount` call.
 */
export default function useMountedMfe(
    mfeKey,
    mfeBasePath,
    rootNode,
    extraMountParams = { mfeConfig: {}, mfeUtilities: {} }
) {
    const { mfe: mfeModule } = useMfe(mfeKey, true)
    const { i18n } = useTranslations('common', mfeKey)
    const { language } = i18n
    const prevLang = useRef(language)
    const translations = useMfeTranslations(mfeKey)('common', 'common')
    const [mountedModule, setMountedModule] = useState(null)
    const [error, setError] = useState(null)
    const history = useHistory()
    const userData = useUserData()
    const {
        setTitle,
        setBreadcrumbs,
        pageHeaderActionsRef,
        pageHeaderTitleExtrasRef,
    } = useContext(PageHeaderContext)
    const notifier = useNotifier()
    const theme = useContext(ThemeContext)

    const mappedTranslations = useMemo(
        () => mapTranslations(translations),
        [translations]
    )

    useEffect(() => {
        const languageChanged = language !== prevLang.current
        prevLang.current = language
        if (!mfeModule || !rootNode || (mountedModule && !languageChanged)) {
            return
        }
        // We remount if the language changed. Make sure to also unmount.
        if (languageChanged && mfeModule.unmount) {
            mfeModule.unmount(rootNode)
        }
        mfeModule
            .mount(rootNode, {
                history,
                ...extraMountParams,
                mfeConfig: {
                    ...(extraMountParams?.mfeConfig ?? {}),
                    userData,
                    basePath: mfeBasePath,
                    translations: mappedTranslations,
                    language: language,
                    i18n: i18n,
                    theme: {
                        themeImages: theme?.activeTheme?.config,
                    },
                },
                mfeUtilities: {
                    ...(extraMountParams?.mfeUtilities ?? {}),
                    setTitle: setTitle,
                    setBreadcrumbs: setBreadcrumbs,
                    pageHeaderActionsContainer: pageHeaderActionsRef?.current,
                    pageHeaderTitleExtras: pageHeaderTitleExtrasRef?.current,
                    permissions: {
                        isAllAllowed: permissions => {
                            return isAllAllowed(userData, permissions)
                        },
                        isAllowed: permission => {
                            return isAllowed(userData, permission)
                        },
                        isAnyAllowed: permissions => {
                            return isAnyAllowed(userData, permissions)
                        },
                        isGlobalAdmin: () => {
                            return isGlobalAdmin(userData)
                        },
                        ...(extraMountParams?.mfeUtilities?.permissions ?? {}),
                    },
                    onError: e => {
                        console.error(e)
                        setError(e)
                    },
                    onNotFound: e => {
                        console.log('Not found: ', e)
                        history.push(reverse('not-found'))
                    },
                    onUnauthorized: () => {
                        history.push(reverse('unauthorized'))
                    },
                    createMfeLink,
                    CROSS_MFE_LINKS,
                    notifier,
                },
            })
            .then(m => {
                setMountedModule(m)
            })
    }, [
        mfeModule,
        history,
        mfeBasePath,
        translations,
        userData,
        mountedModule,
        setBreadcrumbs,
        setTitle,
        rootNode,
        extraMountParams,
        language,
        mappedTranslations,
        pageHeaderActionsRef,
        pageHeaderTitleExtrasRef,
        notifier,
        i18n,
        theme,
    ])

    useEffect(() => {
        if (!mountedModule || !mountedModule?.updateHistory) {
            return
        }
        return history.listen(location => {
            mountedModule.updateHistory(location)
        })
    }, [mountedModule, history])

    // Make sure we unmount when leaving the route.
    useEffect(() => {
        return () => {
            if (mfeModule?.unmount) {
                mfeModule.unmount(rootNode)
            }
        }
    }, [mfeModule, rootNode])

    useEffect(() => {
        return () => {
            // Clear the title when leaving a MFE.
            if (setTitle && typeof setTitle === 'function') {
                //This extra check is made to prevent the notifications mfe unmounting error in drawer mode
                setTitle('')
            }
        }
    }, [setTitle])

    if (error) {
        setError(null)
        throw error
    }

    return mountedModule
}
